import { encodeWav, interleaveChannels, concatChunks } from './wavEncode';
import { dspAssetUrl } from './dspAssetUrl';

/**
 * MicRecorder — microphone → AudioWorklet capture → WAV blob.
 *
 * Raw PCM is captured on the audio thread (recorderProcessor.js) so recordings
 * stay uncompressed and aligned with the transport clock; MediaRecorder's
 * compressed, self-timed output is unsuitable for multitrack work.
 */
export class MicRecorder {
  constructor(audioCtx, options = {}) {
    this._ctx = audioCtx;
    this._deviceId = options.deviceId || 'default';
    this._stream = null;
    this._sourceNode = null;
    this._workletNode = null;
    this._chunks = []; // arrays of per-channel Float32Array
    this._firstFrame = -1;
    this._numChannels = 1;
    this._active = false;
    this._stopWaiters = new Map();
    this._stopToken = 0;
  }

  get active() {
    return this._active;
  }

  /** Request mic access and build the capture graph. Call before start(). */
  async init() {
    if (this._workletNode) return this;

    const deviceConstraint = this._deviceId && this._deviceId !== 'default'
      ? { deviceId: { exact: this._deviceId } }
      : {};
    this._stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        ...deviceConstraint,
        channelCount: { ideal: 2 },
        sampleRate: { ideal: this._ctx.sampleRate },
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false
      }
    });

    try {
      await this._ctx.audioWorklet.addModule(dspAssetUrl('/audio/recorderProcessor.js'));
    } catch (_) {
      // Module may already be registered — addModule is not idempotent-safe
      // across errors, but re-registration errors are ignorable here.
    }

    this._sourceNode = this._ctx.createMediaStreamSource(this._stream);
    this._numChannels = Math.max(1, Math.min(2, this._sourceNode.channelCount || 1));
    this._workletNode = new AudioWorkletNode(this._ctx, 'cookup-recorder', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: this._numChannels
    });

    this._workletNode.port.onmessage = ({ data }) => {
      if (data.type === 'chunk' && Array.isArray(data.channels)) {
        // Transferred Float32Arrays arrive intact; no main-thread copy needed.
        if (this._firstFrame < 0 && Number.isFinite(data.firstFrame)) this._firstFrame = data.firstFrame;
        this._chunks.push(data.channels);
      } else if (data.type === 'stopped') {
        const resolve = this._stopWaiters.get(data.token);
        this._stopWaiters.delete(data.token);
        resolve?.();
      }
    };

    // Keep the worklet pulled by the graph without making the mic audible.
    this._silentSink = this._ctx.createGain();
    this._silentSink.gain.value = 0;
    this._sourceNode.connect(this._workletNode);
    this._workletNode.connect(this._silentSink);
    this._silentSink.connect(this._ctx.destination);

    return this;
  }

  start() {
    if (!this._workletNode) throw new Error('MicRecorder.init() first');
    this._chunks = [];
    this._firstFrame = -1;
    this._active = true;
    this._workletNode.port.postMessage({ type: 'start' });
  }

  /**
   * Round-trip latency to remove from a take, in seconds.
   *
   * A performer hears the transport outputLatency after the engine renders
   * it, and their sound reaches the capture worklet an input latency later,
   * so a raw take is late by both. Browsers that report the capture track's
   * latency are trusted; otherwise baseLatency stands in for the input side.
   */
  roundTripLatencySeconds() {
    const ctx = this._ctx;
    const settings = this._stream?.getAudioTracks?.()[0]?.getSettings?.() || {};
    const inputLatency = Number.isFinite(settings.latency) ? settings.latency : (ctx.baseLatency || 0);
    const outputLatency = ctx.outputLatency || ctx.baseLatency || 0;
    return Math.max(0, inputLatency + outputLatency);
  }

  /**
   * Stop capturing and resolve { blob, durationSeconds, sampleRate, startTime }.
   * startTime is the context time of the take's first kept sample, before
   * latency compensation. `leadingTrimSeconds(startTime, roundTripSeconds)`
   * may name how much of the head to discard. Waits for the worklet to flush its partial chunk so the tail
   * of the take is never dropped.
   */
  async stop({ leadingTrimSeconds = null } = {}) {
    if (!this._workletNode || !this._active) return null;
    this._active = false;
    const token = ++this._stopToken;
    await new Promise((resolve) => {
      this._stopWaiters.set(token, resolve);
      this._workletNode.port.postMessage({ type: 'stop', token });
      // A suspended context never answers; do not hang the caller.
      setTimeout(() => {
        if (this._stopWaiters.delete(token)) resolve();
      }, 500);
    });

    const numChannels = this._chunks[0]?.length || this._numChannels || 1;
    const perChannel = [];
    for (let ch = 0; ch < numChannels; ch += 1) {
      perChannel.push(concatChunks(this._chunks.map((chunk) => chunk[ch])));
    }
    this._chunks = [];

    // The caller decides how much of the head precedes the transport once
    // round-trip latency is removed; those samples were never performed
    // against the music and would push the take late.
    const startTime = this._firstFrame >= 0 ? this._firstFrame / this._ctx.sampleRate : null;
    const trimSeconds = typeof leadingTrimSeconds === 'function'
      ? Math.max(0, Number(leadingTrimSeconds(startTime, this.roundTripLatencySeconds())) || 0)
      : 0;
    const trimFrames = Math.min(perChannel[0]?.length || 0, Math.round(trimSeconds * this._ctx.sampleRate));
    if (trimFrames > 0) {
      for (let ch = 0; ch < perChannel.length; ch += 1) perChannel[ch] = perChannel[ch].subarray(trimFrames);
    }

    const frames = perChannel[0]?.length || 0;
    if (frames === 0) return null;

    const interleaved = interleaveChannels(perChannel);
    // Capture is 32-bit float, matching the Web Audio engine exactly. A take is
    // re-imported into the session and mixed, so quantizing on the way in would
    // bake a permanent loss into the project — the one place fixed-point has no
    // business being. Pro Tools records 24-bit or 32-float for the same reason.
    const wav = encodeWav(interleaved, {
      sampleRate: this._ctx.sampleRate,
      numChannels,
      bitDepth: 32
    });

    return {
      blob: new Blob([wav], { type: 'audio/wav' }),
      durationSeconds: frames / this._ctx.sampleRate,
      sampleRate: this._ctx.sampleRate,
      startTime: startTime === null ? null : startTime + trimFrames / this._ctx.sampleRate
    };
  }

  /** Release the mic and tear down the capture graph. */
  dispose() {
    this._active = false;
    try { this._workletNode?.port.postMessage({ type: 'stop' }); } catch (_) { /* noop */ }
    try { this._sourceNode?.disconnect(); } catch (_) { /* noop */ }
    try { this._workletNode?.disconnect(); } catch (_) { /* noop */ }
    try { this._silentSink?.disconnect(); } catch (_) { /* noop */ }
    this._stream?.getTracks().forEach((track) => track.stop());
    this._stream = null;
    this._sourceNode = null;
    this._workletNode = null;
    this._silentSink = null;
    this._chunks = [];
    this._stopWaiters.forEach((resolve) => resolve());
    this._stopWaiters.clear();
  }
}
